import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import PageLayout from "../../components/PageLayout";
import Table from "../../components/Table";
import ActionLoader from "../../components/ActionLoader";
import Toast from "../../components/Toast";

import { GET_CONVERTER_TESTING_REPORT_URL, getData } from "../../utils/api";
import { getLoginDetails, canAccessTestingReports } from "../../utils/storage";

import InsightsHeaderIcon from "../../icons/newicons/insights.svg";

import "../Converters/converters.css";
import "./insights.css";
import "./converter-testing.css";
import { CoverageExecutivePanel, CatalogAssayExecutivePanel, ReportExecutivePanel } from "./ConverterTestingCharts";
import { AssayDriftBuckets, AssayDriftOemList } from "./AssayDriftView";
import { AssayDriftPrioritySection } from "./AssayDriftPrioritySection";
import { AssayQualityBuckets, AssayQualityOemList } from "./AssayQualityView";
import { AssayHeatMapGrid, AssayHeatMapOemList } from "./AssayHeatMapView";
import { ReportInfoIcon } from "./ReportInfoIcon";
import { InsightsSubNav } from "./InsightsSubNav";

const PAGE_SIZE = 10;

const CONVERTER_TYPES = [
  { value: "", label: "All types" },
  { value: "CERAMIC", label: "Ceramic" },
  { value: "DIESEL", label: "Diesel" },
  { value: "AFTER MKT", label: "After Market" },
  { value: "DOMESTIC WIRE", label: "Domestic Wire" },
  { value: "FOREIGN WIRE", label: "Foreign Wire" },
];

const REPORT_TABS = [
  {
    id: "coverage",
    label: "Testing Coverage",
    description:
      "Shows what share of the converter catalog has at least one lab test. A converter counts as tested when a Test record exists for its product ID.",
  },
  {
    id: "catalogAssay",
    label: "Catalog Assay",
    description:
      "Shows what share of the catalog has assay values on file (subweight > 0 and at least one of Pt, Pd, Rh > 0). Includes a quick glance of converters still missing catalog assay.",
  },
  {
    id: "markedForTest",
    label: "Marked for Test",
    description:
      "Lists converters flagged for lab testing (isMarkForTest). Use this to track the testing queue and see which marked units still have no test data.",
  },
  {
    id: "assayDrift",
    label: "Assay Drift",
    description:
      "Compares catalog assay (subweight, Pt, Pd, Rh) to the average across lab tests. Drift % = |test average − catalog| ÷ catalog × 100. Click a bucket to list OEMs in that range.",
  },
  {
    id: "assayHeatMap",
    label: "Assay Heat Map",
    description:
      "Executive view of average catalog-vs-test drift by converter type and assay field. Green = low drift, red = very high. Click any cell to drill into OEMs for that type and field.",
  },
  {
    id: "topTested",
    label: "Top Tested",
    description:
      "Ranks converters by how many lab tests they have. Highlights the most frequently tested units in the catalog.",
  },
  {
    id: "assayQuality",
    label: "Assay Quality",
    description:
      "Measures consistency across repeat tests (requires 2+ tests per converter). Variability % = std dev ÷ test average × 100. Higher values mean less consistent test results.",
  },
  {
    id: "noAssay",
    label: "No Assay",
    description:
      "Converters missing catalog assay values (subweight is 0, or Pt, Pd, and Rh are all 0). Includes units with no tests yet and units that have lab tests but still need catalog assay updated from test data.",
  },
];

const NO_ASSAY_TEST_FILTERS = [
  { value: "", label: "All" },
  { value: "withTests", label: "Has tests — needs catalog update" },
  { value: "withoutTests", label: "No tests yet" },
];

const ACTIVE_REPORT = REPORT_TABS.reduce((acc, tab) => {
  acc[tab.id] = tab;
  return acc;
}, {});

function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatDate(ms) {
  if (!ms) return "—";
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseReportResponse(res, reportType) {
  const inner = res?.data != null ? res.data : res;
  if (reportType === "coverage" || reportType === "catalogAssay") {
    return {
      summary: inner?.summary || null,
      list: [],
      totalCount: inner?.summary?.total ?? 0,
      areMoreItemsAvailable: false,
    };
  }
  return {
    summary: inner?.summary || null,
    list: inner?.list || [],
    totalCount: inner?.totalCount ?? inner?.summary?.totalCount ?? 0,
    areMoreItemsAvailable: Boolean(inner?.areMoreItemsAvailable),
  };
}

function paginationLabel(offset, pageSize, totalCount, pageLength) {
  if (!totalCount) return "No records";
  const from = totalCount === 0 ? 0 : offset + 1;
  const to = offset + pageLength;
  return `Showing ${from}–${to} of ${totalCount.toLocaleString()}`;
}

function CoverageSummary({ summary }) {
  if (!summary) return null;

  const byType = summary.byType || [];

  return (
    <>
      <div className="converter-testing-summary-grid">
        <div className="converter-testing-summary-card">
          <div className="converter-testing-summary-value">{summary.total ?? 0}</div>
          <div className="converter-testing-summary-label">Total converters</div>
        </div>
        <div className="converter-testing-summary-card">
          <div className="converter-testing-summary-value">{summary.tested ?? 0}</div>
          <div className="converter-testing-summary-label">Tested</div>
        </div>
        <div className="converter-testing-summary-card">
          <div className="converter-testing-summary-value">{summary.untested ?? 0}</div>
          <div className="converter-testing-summary-label">Untested</div>
        </div>
        <div className="converter-testing-summary-card">
          <div className="converter-testing-summary-value">
            {summary.coveragePct != null ? `${summary.coveragePct}%` : "—"}
          </div>
          <div className="converter-testing-summary-label">Coverage</div>
        </div>
      </div>

      <div className="insights-metal-table-wrap">
        <div className="c-red fw-bolder mb-10 fs-14">Coverage by type</div>
        <table className="insights-metal-table">
          <thead>
            <tr>
              <th scope="col">Type</th>
              <th scope="col" className="t-right">Total</th>
              <th scope="col" className="t-right">Tested</th>
              <th scope="col" className="t-right">Untested</th>
              <th scope="col" className="t-right">Coverage %</th>
            </tr>
          </thead>
          <tbody>
            {byType.map((row) => (
              <tr key={row.type}>
                <th scope="row" className="insights-metal-table-type">{row.type}</th>
                <td className="t-right">{row.total ?? 0}</td>
                <td className="t-right">{row.tested ?? 0}</td>
                <td className="t-right">{row.untested ?? 0}</td>
                <td className="t-right">{row.coveragePct != null ? `${row.coveragePct}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function CatalogAssaySummary({ summary }) {
  if (!summary) return null;

  const byType = summary.byType || [];

  return (
    <>
      <div className="converter-testing-summary-grid">
        <div className="converter-testing-summary-card">
          <div className="converter-testing-summary-value">{summary.total ?? 0}</div>
          <div className="converter-testing-summary-label">Total converters</div>
        </div>
        <div className="converter-testing-summary-card">
          <div className="converter-testing-summary-value">{summary.withAssay ?? 0}</div>
          <div className="converter-testing-summary-label">With catalog assay</div>
        </div>
        <div className="converter-testing-summary-card">
          <div className="converter-testing-summary-value">{summary.withoutAssay ?? 0}</div>
          <div className="converter-testing-summary-label">Without catalog assay</div>
        </div>
        <div className="converter-testing-summary-card">
          <div className="converter-testing-summary-value">
            {summary.coveragePct != null ? `${summary.coveragePct}%` : "—"}
          </div>
          <div className="converter-testing-summary-label">Catalog coverage</div>
        </div>
      </div>

      <div className="insights-metal-table-wrap">
        <div className="c-red fw-bolder mb-10 fs-14">Catalog assay by type</div>
        <table className="insights-metal-table">
          <thead>
            <tr>
              <th scope="col">Type</th>
              <th scope="col" className="t-right">Total</th>
              <th scope="col" className="t-right">With assay</th>
              <th scope="col" className="t-right">Without assay</th>
              <th scope="col" className="t-right">Coverage %</th>
            </tr>
          </thead>
          <tbody>
            {byType.map((row) => (
              <tr key={row.type}>
                <th scope="row" className="insights-metal-table-type">{row.type}</th>
                <td className="t-right">{row.total ?? 0}</td>
                <td className="t-right">{row.withAssay ?? 0}</td>
                <td className="t-right">{row.withoutAssay ?? 0}</td>
                <td className="t-right">{row.coveragePct != null ? `${row.coveragePct}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function getTableConfig(activeTab) {
  switch (activeTab) {
    case "markedForTest":
      return {
        columns: ["OEM", "Type", "Family", "Tests", "Marked date"],
        keys: ["oemLink", "type", "family", "testCount", "markedDate"],
        widthClasses: ["flex-1", "wt-120", "flex-1", "wt-80", "wt-120"],
      };
    case "topTested":
      return {
        columns: ["OEM", "Type", "Manufacturer", "Tests"],
        keys: ["oemLink", "type", "manufacturer", "testCount"],
        widthClasses: ["flex-1", "wt-120", "flex-1", "wt-80"],
      };
    case "assayQuality":
      return {
        columns: ["OEM", "Type", "Tests", "Std Subwt", "Std Pt", "Std Pd", "Std Rh"],
        keys: ["oemLink", "type", "testCount", "stdSubweight", "stdPtAssay", "stdPdAssay", "stdRhAssay"],
        widthClasses: ["flex-1", "wt-120", "wt-80", "wt-100", "wt-100", "wt-100", "wt-100"],
      };
    case "noAssay":
      return {
        columns: ["OEM", "Type", "Description", "Test status", "Tests"],
        keys: ["oemLink", "type", "description", "testStatusLabel", "testCount"],
        widthClasses: ["flex-1", "wt-120", "flex-1", "wt-140", "wt-80"],
      };
    default:
      return { columns: [], keys: [], widthClasses: [] };
  }
}

function mapListItems(list, activeTab) {
  return list.map((item) => {
    const row = {
      ...item,
      oemLink: (
        <Link className="converter-testing-link" to={`/converter/${item.productId}`}>
          {item.oem || item.productId}
        </Link>
      ),
    };

    if (activeTab === "markedForTest") {
      row.markedDate = formatDate(item.markForTestDate);
    }

    if (activeTab === "assayQuality" && item.stdDev) {
      row.stdSubweight = formatNumber(item.stdDev.subweight);
      row.stdPtAssay = formatNumber(item.stdDev.ptAssay);
      row.stdPdAssay = formatNumber(item.stdDev.pdAssay);
      row.stdRhAssay = formatNumber(item.stdDev.rhAssay);
    }

    if (activeTab === "noAssay") {
      const hasTests = (item.testCount ?? 0) > 0;
      row.testStatusLabel = (
        <span
          className={`converter-testing-test-status ${
            hasTests ? "converter-testing-test-status-action" : ""
          }`}
        >
          {hasTests ? "Has tests" : "No tests"}
        </span>
      );
    }

    return row;
  });
}

const ConverterTesting = () => {
  const loginDetails = getLoginDetails();
  const canViewTestingReports = canAccessTestingReports(loginDetails);

  const [activeTab, setActiveTab] = useState("coverage");
  const [typeFilter, setTypeFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [driftField, setDriftField] = useState("");
  const [driftBucket, setDriftBucket] = useState("");
  const [heatMapType, setHeatMapType] = useState("");
  const [noAssayTestFilter, setNoAssayTestFilter] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [areMoreItemsAvailable, setAreMoreItemsAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastParams, setToastParams] = useState({ message: "", type: "", id: "" });
  const fetchTokenRef = useRef(0);

  const fetchReport = useCallback(async (tab, type, text, pageOffset, field, bucket, testFilter, mapType) => {
    const token = ++fetchTokenRef.current;
    try {
      setIsLoading(true);
      const params = {
        reportType: tab,
        count: PAGE_SIZE,
        offset: pageOffset,
      };
      if (tab !== "assayHeatMap" && type) params.type = type;
      if (text) params.text = text;
      if (tab === "assayDrift" && field && bucket) {
        params.driftField = field;
        params.driftBucket = bucket;
      }
      if (tab === "assayQuality" && field && bucket) {
        params.driftField = field;
        params.driftBucket = bucket;
      }
      if (tab === "assayHeatMap" && mapType && field) {
        params.type = mapType;
        params.driftField = field;
      }
      if (tab === "noAssay" && testFilter) {
        params.testStatus = testFilter;
      }

      const res = await getData(GET_CONVERTER_TESTING_REPORT_URL, params);
      if (token !== fetchTokenRef.current) return;

      const parsed = parseReportResponse(res, tab);
      setSummary(parsed.summary);
      setList(parsed.list);
      setTotalCount(parsed.totalCount);
      setAreMoreItemsAvailable(parsed.areMoreItemsAvailable);
    } catch (err) {
      if (token !== fetchTokenRef.current) return;
      console.log("Converter testing report error", err);
      setSummary(null);
      setList([]);
      setTotalCount(0);
      setAreMoreItemsAvailable(false);
      setToastParams({
        type: "ERROR",
        message: "Unable to load converter testing report. Please try again.",
        id: Date.now(),
      });
    } finally {
      if (token === fetchTokenRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!canViewTestingReports) return;
    fetchReport(activeTab, typeFilter, appliedSearch, offset, driftField, driftBucket, noAssayTestFilter, heatMapType);
  }, [activeTab, typeFilter, appliedSearch, offset, driftField, driftBucket, noAssayTestFilter, heatMapType, canViewTestingReports, fetchReport]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setOffset(0);
    setShowDetails(false);
    setDriftField("");
    setDriftBucket("");
    setHeatMapType("");
    setNoAssayTestFilter("");
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedSearch(searchText.trim());
    setOffset(0);
  };

  const handleClearFilters = () => {
    setTypeFilter("");
    setSearchText("");
    setAppliedSearch("");
    setOffset(0);
    setDriftField("");
    setDriftBucket("");
    setHeatMapType("");
    setNoAssayTestFilter("");
  };

  const handleDriftBucketSelect = (field, bucket) => {
    setDriftField(field);
    setDriftBucket(bucket);
    setOffset(0);
  };

  const handleDriftClear = () => {
    setDriftField("");
    setDriftBucket("");
    setOffset(0);
  };

  const handleHeatMapCellSelect = (type, field) => {
    setHeatMapType(type);
    setDriftField(field);
    setDriftBucket("");
    setOffset(0);
  };

  const handleHeatMapClear = () => {
    setHeatMapType("");
    setDriftField("");
    setOffset(0);
  };

  const tableConfig = getTableConfig(activeTab);
  const tableItems = mapListItems(list, activeTab);

  if (!canViewTestingReports) {
    return (
      <PageLayout heading="Insights" hideSearchBar icon={InsightsHeaderIcon}>
        <InsightsSubNav />
        <div className="c-gray fs-16">You do not have permission to view converter testing reports.</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout heading="Insights" hideSearchBar icon={InsightsHeaderIcon}>
      <InsightsSubNav />
      <div className="converter-testing-tabs">
        {REPORT_TABS.map((tab) => (
          <div
            key={tab.id}
            className={`converter-testing-tab-item ${activeTab === tab.id ? "active" : ""}`}
          >
            <button
              type="button"
              className={`converter-testing-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </button>
            <ReportInfoIcon
              title={tab.label}
              description={tab.description}
              isActive={activeTab === tab.id}
            />
          </div>
        ))}
      </div>

      <div className="converter-testing-report-info">
        <span className="converter-testing-report-info-icon" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="16" height="16" focusable="false">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path fill="currentColor" d="M7.25 7h1.5V11h-1.5V7zm0-2.25h1.5V5.5h-1.5v-.75z" />
          </svg>
        </span>
        <p className="converter-testing-report-info-text">{ACTIVE_REPORT[activeTab]?.description}</p>
      </div>

      <form className="converter-testing-filters" onSubmit={handleApplyFilters}>
        <div className="converter-testing-filter-field converter-testing-filter-type">
          <label htmlFor="converter-type-filter" className="converter-testing-filter-sr">
            Converter type
          </label>
          <select
            id="converter-type-filter"
            value={typeFilter}
            disabled={activeTab === "assayHeatMap"}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setOffset(0);
            }}
          >
            {CONVERTER_TYPES.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="converter-testing-filter-field converter-testing-filter-search">
          <label htmlFor="converter-search" className="converter-testing-filter-sr">
            Search OEM / description
          </label>
          <input
            id="converter-search"
            type="text"
            value={searchText}
            placeholder="Search OEM / description..."
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        {activeTab === "noAssay" && (
          <div className="converter-testing-filter-field converter-testing-filter-test-status">
            <label htmlFor="no-assay-test-filter" className="converter-testing-filter-sr">
              Test status
            </label>
            <select
              id="no-assay-test-filter"
              value={noAssayTestFilter}
              onChange={(e) => {
                setNoAssayTestFilter(e.target.value);
                setOffset(0);
              }}
            >
              {NO_ASSAY_TEST_FILTERS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="converter-testing-filter-actions">
          <button type="submit" className="converter-testing-filter-btn converter-testing-filter-btn-apply">
            Apply
          </button>
          <button
            type="button"
            className="converter-testing-filter-btn converter-testing-filter-btn-clear"
            onClick={handleClearFilters}
          >
            Clear
          </button>
        </div>
      </form>

      {isLoading && <ActionLoader />}

      {!isLoading && activeTab === "coverage" && (
        <>
          <CoverageExecutivePanel summary={summary} />
          <CoverageSummary summary={summary} />
        </>
      )}

      {!isLoading && activeTab === "catalogAssay" && (
        <>
          <CatalogAssayExecutivePanel summary={summary} />
          <CatalogAssaySummary summary={summary} />
        </>
      )}

      {!isLoading && activeTab !== "coverage" && activeTab !== "catalogAssay" && activeTab !== "assayDrift" && activeTab !== "assayHeatMap" && activeTab !== "assayQuality" && summary && (
        <ReportExecutivePanel reportType={activeTab} summary={summary} />
      )}

      {!isLoading && activeTab === "assayDrift" && (
        <>
          <AssayDriftBuckets
            summary={summary}
            selectedField={driftField}
            selectedBucket={driftBucket}
            onSelect={handleDriftBucketSelect}
            onClear={handleDriftClear}
          />
          <AssayDriftPrioritySection summary={summary} />
          {driftField && driftBucket && (
            <AssayDriftOemList
              items={list}
              driftField={driftField}
              totalCount={totalCount}
              offset={offset}
              pageSize={PAGE_SIZE}
              areMoreItemsAvailable={areMoreItemsAvailable}
              onPrev={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
              onNext={() => setOffset((prev) => prev + PAGE_SIZE)}
            />
          )}
        </>
      )}

      {!isLoading && activeTab === "assayHeatMap" && (
        <>
          <AssayHeatMapGrid
            summary={summary}
            selectedType={heatMapType}
            selectedField={driftField}
            onSelect={handleHeatMapCellSelect}
            onClear={handleHeatMapClear}
          />
          {heatMapType && driftField && (
            <AssayHeatMapOemList
              items={list}
              heatMapType={heatMapType}
              driftField={driftField}
              totalCount={totalCount}
              offset={offset}
              pageSize={PAGE_SIZE}
              areMoreItemsAvailable={areMoreItemsAvailable}
              onPrev={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
              onNext={() => setOffset((prev) => prev + PAGE_SIZE)}
            />
          )}
        </>
      )}

      {!isLoading && activeTab === "assayQuality" && (
        <>
          <AssayQualityBuckets
            summary={summary}
            selectedField={driftField}
            selectedBucket={driftBucket}
            onSelect={handleDriftBucketSelect}
            onClear={handleDriftClear}
          />
          {driftField && driftBucket && (
            <AssayQualityOemList
              items={list}
              qualityField={driftField}
              totalCount={totalCount}
              offset={offset}
              areMoreItemsAvailable={areMoreItemsAvailable}
              onPrev={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
              onNext={() => setOffset((prev) => prev + PAGE_SIZE)}
            />
          )}
        </>
      )}

      {!isLoading && activeTab !== "coverage" && activeTab !== "catalogAssay" && activeTab !== "assayDrift" && activeTab !== "assayHeatMap" && activeTab !== "assayQuality" && (
        <>
          <button
            type="button"
            className="converter-testing-details-toggle"
            onClick={() => setShowDetails((v) => !v)}
          >
            {showDetails ? "▾ Hide detailed records" : "▸ View detailed records"}
            {totalCount > 0 ? ` (${totalCount.toLocaleString()} converters)` : ""}
          </button>
          {showDetails && (
            <>
              {tableItems.length === 0 ? (
                <div className="c-gray fs-14">No converters match this report.</div>
              ) : (
                <>
                  <div className="converter-testing-details-meta">
                    {paginationLabel(offset, PAGE_SIZE, totalCount, tableItems.length)}
                  </div>
                  <Table
                    scrollTable
                    columns={tableConfig.columns}
                    items={tableItems}
                    keys={tableConfig.keys}
                    widthClasses={tableConfig.widthClasses}
                    offset={offset}
                    limit={PAGE_SIZE}
                    areMoreItemsAvailable={areMoreItemsAvailable}
                    fetchMore={() => setOffset((prev) => prev + PAGE_SIZE)}
                    fetchLess={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
                  />
                </>
              )}
            </>
          )}
        </>
      )}

      <Toast
        message={toastParams.message}
        type={toastParams.type}
        id={toastParams.id}
      />
    </PageLayout>
  );
};

export default ConverterTesting;
